import { type GetServerSideProps } from "next";
import Image from "next/image";
import Link from "next/link";
import { useCallback, useState } from "react";
import {
  CreditCard,
  PaymentForm,
  type PaymentFormProps,
} from "react-square-web-payments-sdk";
import { Layout } from "~/components/Layout/Layout";
import { Private } from "~/components/Private";
import { env } from "~/env.mjs";
import { EventNames } from "~/mixpanel/EventNames";
import { trackEvent } from "~/mixpanel/trackEvent";
import { api } from "~/utils/api";

const Donate = () => {
  const { description, error, imgUrl, setTab, tab } = useDonations();

  return (
    <Layout title="Donate" description={description} imgUrl={imgUrl}>
      {error && (
        <div className="rounded-xl bg-red-500 p-4">{error.message}</div>
      )}
      <Tabs tab={tab} onTabChange={setTab} />
      <div className="overflow-x-hidden">
        {tab === "Pledge" && <PledgeTabBody />}
        {tab === "Cash App" && <CashAppTabBody />}
        {tab === "PayPal" && <PayPalTabBody />}
        {tab === "Zelle" && <ZelleTabBody />}
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  await trackEvent(ctx, EventNames.DONATE_PAGE_VIEWED);
  return { props: {} };
};

type TabName = "Pledge" | "Cash App" | "PayPal" | "Zelle";
const Tabs = ({
  tab,
  onTabChange,
}: {
  tab: TabName;
  onTabChange: (tab: TabName) => void;
}) => {
  return (
    <div role="tablist" className="tabs-bordered tabs my-6 justify-between">
      <Tab
        name="Pledge"
        isActive={tab === "Pledge"}
        onClick={() => onTabChange("Pledge")}
      />
      <Tab
        name="Cash App"
        isActive={tab === "Cash App"}
        onClick={() => onTabChange("Cash App")}
      />
      <Tab
        name="PayPal"
        isActive={tab === "PayPal"}
        onClick={() => onTabChange("PayPal")}
      />
      <Tab
        name="Zelle"
        isActive={tab === "Zelle"}
        onClick={() => onTabChange("Zelle")}
      />
    </div>
  );
};
const Tab = ({
  name,
  isActive,
  onClick,
}: {
  name: TabName;
  isActive: boolean;
  onClick: () => void;
}) => (
  <a
    onClick={onClick}
    role="tab"
    className={`${isActive ? "tab-active" : ""} tab  px-0 no-underline`}
  >
    {name}
  </a>
);
const useDonations = () => {
  const pledge = api.donate.pledge.useMutation();
  const [dollarsPerMonth, setDollarsPerMonth] = useState(20);
  const [months, setMonths] = useState(3);
  const [artificialDelay, setArtificialDelay] = useState(false);
  const [tab, setTab] = useState<TabName>("Pledge");

  const existingPledge = api.donate.getPledge.useQuery();

  const onSubmit: PaymentFormProps["cardTokenizeResponseReceived"] =
    useCallback(
      async ({ details, token, errors }) => {
        if (errors) throw errors;
        if (!token || !details?.card)
          throw new Error("Missing token or details");
        await pledge.mutateAsync({
          dollarsPerMonth: dollarsPerMonth,
          months,
          sourceId: token,
          cardDetails: {
            cardBrand: details.card.brand,
            expMonth: BigInt(details.card.expMonth),
            expYear: BigInt(details.card.expYear),
            last4: details.card.last4,
          },
        });
        setArtificialDelay(true);
        setTimeout(async () => {
          setArtificialDelay(false);
          await existingPledge.refetch();
        }, 30000);
      },
      [dollarsPerMonth, existingPledge, months, pledge]
    );

  const error = existingPledge.error || pledge.error;
  const loading =
    existingPledge.isLoading || pledge.isLoading || artificialDelay;

  const description =
    "Our Islamic Library is a treasure trove of knowledge that benefits not only our users but also the greater community. By donating to our library, users help ensure that we can continue to provide a wealth of resources and services to those in need. We offer the option to donate once or to set up a monthly automatic donation, making it easy for users to support our mission to spread the values and teachings of Islam. Every donation, big or small, helps us to provide the best possible services to our users, so we hope you will join us in supporting our mission by donating today!";
  const imgUrl = "/img/donate.jpg";

  return {
    dollarsPerMonth,
    setDollarsPerMonth,
    months,
    setMonths,
    existingPledge,
    onSubmit,
    error,
    loading,
    description,
    imgUrl,
    tab,
    setTab,
  };
};
const DonatePage = () => (
  <Private>
    <Donate />
  </Private>
);
const PledgeTabBody = () => {
  const {
    existingPledge,
    onSubmit,
    dollarsPerMonth,
    setDollarsPerMonth,
    months,
    setMonths,
    loading,
    error,
  } = useDonations();

  if (existingPledge?.data?.existingPledge) {
    const { pricePerCadence } = existingPledge.data.existingPledge;
    const periods = existingPledge.data.existingPledge.periods as number;
    return (
      <span>
        Thank you for your donation of{" "}
        <strong>
          ${pricePerCadence / 100} {periods > 1 && ` for ${periods} month(s)`}!
        </strong>{" "}
        You can manage your donation in your{" "}
        <Link href="/my-account">my account page</Link>.
      </span>
    );
  }

  return (
    <>
      <h2 className="mt-0">Monthly Pledge</h2>
      <PaymentForm
        applicationId={env.NEXT_PUBLIC_SQUARE_APP_ID}
        locationId={env.NEXT_PUBLIC_SQUARE_LOCATION_ID}
        cardTokenizeResponseReceived={onSubmit}
      >
        {loading && (
          <div className="alert alert-info">Loading (up to 30 seconds)...</div>
        )}

        {error && (
          <div className="alert alert-error">
            <div>Error:</div>
            <div>{error.message}</div>
          </div>
        )}

        <div className="flex flex-col justify-stretch gap-6">
          {/* Amount */}
          <div className="flex flex-col text-center">
            <label>Amount</label>
            <div className="flex items-center gap-2">
              <span className="text-xl">$</span>
              <input
                className="input w-full border border-blue-500"
                type="number"
                min={1}
                value={dollarsPerMonth}
                onChange={(e) => setDollarsPerMonth(Number(e.target.value))}
              />
            </div>
          </div>

          {/* Months */}
          <div className="flex flex-col items-center">
            <label>Months</label>
            <input
              className="input w-full border border-blue-500"
              type="number"
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              min={1}
            />
          </div>
          <div className={loading ? "hidden" : ""}>
            <CreditCard />
          </div>
        </div>
      </PaymentForm>
    </>
  );
};
const CashAppTabBody = () => {
  return (
    <div>
      <h2 className="mt-0">Cash App</h2>
      <p>
        You can donate to our Cash App account at{" "}
        <Link href="https://cash.app/$IslamiclibraryNJ" target="_blank">
          <strong>$IslamicLibrary</strong>.
        </Link>
      </p>
      <p>Or click or scan below to donate to our Cash App:</p>
      <QRCode
        alt="Cash App"
        href="https://cash.app/$IslamiclibraryNJ"
        imgUrl="https://cash.app/qr/$IslamiclibraryNJ"
      />
    </div>
  );
};
const PayPalTabBody = () => {
  return (
    <div>
      <h2 className="mt-0">PayPal</h2>
      <p>
        You can send your donations to{" "}
        <strong>IslamicReminders1@gmail.com</strong>. Click or scan the QR code
        below to send your donation via PayPal.
      </p>
      <QRCode
        alt="PayPal"
        href="https://www.paypal.com/myaccount/transfer/homepage"
        imgUrl="/img/qrcode_www.paypal.com.png"
      />
    </div>
  );
};
const ZelleTabBody = () => {
  return (
    <div>
      <h2 className="mt-0">Zelle</h2>
      <p>
        You can send your donations to <strong>admin@iplnj.com</strong> via
        Zelle. Click or scan below to get started with Zelle.
      </p>
      <QRCode
        alt="Zelle"
        imgUrl="/img/qrcode_www.zellepay.com.png"
        href="https://www.zellepay.com/get-started"
      />
    </div>
  );
};
const QRCode = ({
  alt,
  href,
  imgUrl,
}: {
  href: string;
  imgUrl: string;
  alt: TabName;
}) => {
  return (
    <div className="flex flex-col gap-5 text-center">
      <div className="flex justify-center rounded-3xl bg-white">
        <Link href={href} target="_blank">
          <Image
            src={imgUrl}
            width={350}
            height={350}
            alt={alt}
            className="m-0 p-4"
          />
        </Link>
      </div>
      <Link href={href} target="_blank">
        {href}
      </Link>
    </div>
  );
};
export default DonatePage;
